/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ContentChildren, Directive, ElementRef, Input, QueryList } from '@angular/core';

@Directive({
    selector: '[appMenuItem]',
})
export class MenuItemDirective {
    /**
     * Get the child menu items
     */
    @ContentChildren(MenuItemDirective, { descendants: true })
    public content: QueryList<MenuItemDirective>;

    /**
     * Enable/Disable
     */
    @Input() public disabled: boolean;

    /**
     * Pass the ID through to the html of the menu
     */
    @Input() public id: string;

    /**
     * Pass the class through to the html of the menu
     */
    @Input() public class: string;

    /**
     * Router navigation location
     */
    @Input() public loc: string;

    /**
     * Specify function to be called
     */
    @Input() public callBack: (...args: any[]) => void;

    /**
     * Specify whether the item is hidden
     */
    @Input() public hidden: boolean;

    /**
     * Specify modal to launch if stated
     */
    @Input() public modal: string;

    /**
     * Sub Group heading/title
     */
    @Input() public subHeading: string;

    /**
     * Determines whether opened in a new tab or not
     */
    @Input() public targetNew: boolean;

    /**
     * Menu sub groups
     */
    @Input() public subGroup: boolean;

    /**
     * Menu item image
     */
    @Input() public iconName: string;

    /**
     * Show or hide the subscription icon.
     */
    @Input() public subscriptionRequired: boolean = false;

    /**
     * Store the hidden state of the subgroups.
     */
    public subGroupHidden: boolean;

    constructor(
        private elRef: ElementRef
    ) {
    }

    /**
     * Variable heading getter for returning a menu item's heading/title.
     * @returns {string}
     */
    get heading(): string {
        if (this.subHeading) {
            return this.subHeading;
        }

        const parser = new DOMParser();
        const heading = parser.parseFromString(
            this.elRef.nativeElement.innerHTML,
            'text/html'
        );

        return heading.body.textContent.trim();
    }

    /**
     * Items variable getter that returns each menu item.
     * @returns {MenuItemDirective[]}
     */
    get subItems(): MenuItemDirective[] {
        return this.content.toArray().filter(item => !item.subHeading);
    }
}
